import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Package, Eye, EyeOff } from 'lucide-react';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { VariantsEditor } from '@/components/admin/VariantsEditor';
import { CategorySelect } from '@/components/admin/CategorySelect';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    images: [],
    variants: [],
    ingredients: '',
    stock: '',
    category_id: null
  });

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // Hide a product from the shop (or show it again) without deleting it
  const handleToggleVisible = async (product) => {
    const visible = product.is_active === false;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/products/${product.product_id}`, { is_active: visible }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(visible ? 'Shown in the shop' : 'Hidden from the shop');
      fetchProducts();
    } catch (error) {
      console.error('Failed to change visibility:', error);
      toast.error('Failed to change visibility');
    }
  };

  // Quick change from the table: move a product into a category (or out of all)
  const handleCategoryChange = async (product, categoryId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/products/${product.product_id}/category`, { category_id: categoryId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const name = categories.find((c) => c.category_id === categoryId)?.name;
      toast.success(name ? `Moved to ${name}` : 'Removed from category');
      fetchProducts();
      fetchCategories();
    } catch (error) {
      console.error('Failed to change category:', error);
      toast.error(error.response?.data?.detail || 'Failed to change category');
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      image_url: product.image_url,
      images: product.images && product.images.length > 0 ? product.images : (product.image_url ? [product.image_url] : []),
      variants: product.variants || [],
      ingredients: product.ingredients,
      stock: product.stock.toString(),
      category_id: product.category_id || null
    });
    setEditDialog(true);
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      image_url: '',
      images: [],
      variants: [],
      ingredients: '',
      stock: '100',
      category_id: null
    });
    setAddDialog(true);
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const images = formData.images || [];
      const updateData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        image_url: images[0] || formData.image_url,
        images: images,
        variants: (formData.variants || []).filter((v) => v.label && v.label.trim()),
        ingredients: formData.ingredients,
        stock: parseInt(formData.stock),
      };
      // Only send the category when it was changed, so older products that
      // only have a category slug keep it
      if ((formData.category_id || null) !== (selectedProduct.category_id || null)) {
        updateData.category_id = formData.category_id || null;
      }

      await axios.put(`${API}/admin/products/${selectedProduct.product_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Product updated successfully');
      setEditDialog(false);
      fetchProducts();
      fetchCategories();
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error('Failed to update product');
    }
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const images = formData.images || [];
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        image_url: images[0] || formData.image_url,
        images: images,
        variants: (formData.variants || []).filter((v) => v.label && v.label.trim()),
        ingredients: formData.ingredients,
        stock: parseInt(formData.stock) || 0,
        category_id: formData.category_id || null
      };

      await axios.post(`${API}/products`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Product created successfully');
      setAddDialog(false);
      fetchProducts();
      fetchCategories();
    } catch (error) {
      console.error('Failed to create product:', error);
      toast.error('Failed to create product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  const [recomputing, setRecomputing] = useState(false);

  const handleRecomputePrices = async () => {
    const markupStr = window.prompt('Apply this EUR markup to every CJ-imported product?', '8.50');
    if (markupStr === null) return;
    const markup = parseFloat(markupStr);
    if (Number.isNaN(markup) || markup < 0) {
      toast.error('Enter a valid non-negative number');
      return;
    }
    setRecomputing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${API}/admin/products/recompute-prices`,
        { markup_eur: markup },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${res.data.updated} products with €${markup} markup`);
      fetchProducts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to recompute prices');
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 md:p-8" data-testid="admin-products">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Products</h1>
            <p className="text-muted-foreground">Manage your product inventory</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleRecomputePrices}
              disabled={recomputing}
              variant="outline"
              data-testid="recompute-prices-btn"
            >
              {recomputing ? 'Updating…' : 'Recompute CJ prices'}
            </Button>
            <Button
              onClick={handleAdd}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Loading products...</p>
          </div>
        ) : (
          <div className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-semibold text-foreground">Product</th>
                    <th className="text-left p-4 font-semibold text-foreground">Cost (CJ)</th>
                    <th className="text-left p-4 font-semibold text-foreground">Price</th>
                    <th className="text-left p-4 font-semibold text-foreground">Margin</th>
                    <th className="text-left p-4 font-semibold text-foreground">Stock</th>
                    <th className="text-left p-4 font-semibold text-foreground">Category</th>
                    <th className="text-right p-4 font-semibold text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const cost = product.cj_sell_price;
                    const hasCost = typeof cost === 'number' && cost > 0;
                    const margin = hasCost ? product.price - cost : null;
                    const marginPct = hasCost && cost > 0 ? ((margin / cost) * 100) : null;
                    return (
                    <tr key={product.product_id} className={`border-t border-border hover:bg-muted/50 ${product.is_active === false ? 'opacity-50' : ''}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                          <div>
                            <p className="font-medium text-foreground">{product.name}</p>
                            {product.is_active === false && <p className="text-xs font-semibold text-muted-foreground">Hidden from shop</p>}
                            <p className="text-sm text-muted-foreground line-clamp-1">{product.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground text-sm" data-testid={`cost-${product.product_id}`}>
                        {hasCost ? (
                          <>
                            <span className="font-semibold text-foreground">€{cost.toFixed(2)}</span>
                            {product.cj_pid && <span className="block text-[10px] text-muted-foreground/70">CJ supplier</span>}
                          </>
                        ) : (
                          <span className="text-muted-foreground/60">—</span>
                        )}
                      </td>
                      <td className="p-4 text-foreground font-semibold" data-testid={`price-${product.product_id}`}>
                        €{product.price.toFixed(2)}
                      </td>
                      <td className="p-4 text-sm" data-testid={`margin-${product.product_id}`}>
                        {hasCost ? (
                          <span className={`font-semibold ${margin >= 5 ? 'text-green-500' : margin > 0 ? 'text-amber-500' : 'text-red-500'}`}>
                            +€{margin.toFixed(2)}
                            <span className="block text-[10px] text-muted-foreground/80 font-normal">{marginPct.toFixed(0)}%</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground/60">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          product.stock < 20 ? 'bg-orange-500/10 text-orange-500' : 'bg-green-500/10 text-green-500'
                        }`}>
                          {product.stock} units
                        </span>
                      </td>
                      <td className="p-4">
                        <CategorySelect
                          value={product.category_id}
                          categories={categories}
                          onChange={(id) => handleCategoryChange(product, id)}
                          aria-label={`Category of ${product.name}`}
                          data-testid={`category-select-${product.product_id}`}
                          className="min-w-[10rem]"
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleVisible(product)}
                            title={product.is_active === false ? 'Show in shop' : 'Hide from shop'}
                            aria-label={product.is_active === false ? `Show ${product.name} in shop` : `Hide ${product.name} from shop`}
                            data-testid={`toggle-visible-${product.product_id}`}
                            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          >
                            {product.is_active === false ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => handleEdit(product)}
                            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.product_id)}
                            className="p-2 rounded-lg hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-500"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold text-foreground">Edit Product</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-name">Product Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-input border-border text-foreground"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full mt-2 px-3 py-2 rounded-lg bg-input border border-border text-foreground"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-price">Price</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="mt-2 bg-input border-border text-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-stock">Stock</Label>
                  <Input
                    id="edit-stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="mt-2 bg-input border-border text-foreground"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-category">Category</Label>
                <CategorySelect
                  id="edit-category"
                  value={formData.category_id}
                  categories={categories}
                  onChange={(id) => setFormData({ ...formData, category_id: id })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Images</Label>
                <div className="mt-2">
                  <ImageUploader
                    value={formData.images}
                    onChange={(imgs) => setFormData({ ...formData, images: imgs })}
                  />
                </div>
              </div>
              <div>
                <Label>Sizes / Variants</Label>
                <div className="mt-2">
                  <VariantsEditor
                    value={formData.variants}
                    onChange={(v) => setFormData({ ...formData, variants: v })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-ingredients">Ingredients</Label>
                <textarea
                  id="edit-ingredients"
                  value={formData.ingredients}
                  onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                  rows={3}
                  className="w-full mt-2 px-3 py-2 rounded-lg bg-input border border-border text-foreground"
                />
              </div>
              <div className="flex gap-4 pt-4 sticky bottom-0 bg-card border-t border-border -mx-6 px-6 pb-2 mt-2">
                <Button onClick={handleUpdate} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-product-btn">
                  Save Changes
                </Button>
                <Button onClick={() => setEditDialog(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Dialog */}
        <Dialog open={addDialog} onOpenChange={setAddDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold text-foreground">Add New Product</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="add-name">Product Name</Label>
                <Input
                  id="add-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-input border-border text-foreground"
                />
              </div>
              <div>
                <Label htmlFor="add-description">Description</Label>
                <textarea
                  id="add-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full mt-2 px-3 py-2 rounded-lg bg-input border border-border text-foreground"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="add-price">Price</Label>
                  <Input
                    id="add-price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="mt-2 bg-input border-border text-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="add-stock">Initial Stock</Label>
                  <Input
                    id="add-stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="mt-2 bg-input border-border text-foreground"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="add-category">Category</Label>
                <CategorySelect
                  id="add-category"
                  value={formData.category_id}
                  categories={categories}
                  onChange={(id) => setFormData({ ...formData, category_id: id })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Images</Label>
                <div className="mt-2">
                  <ImageUploader
                    value={formData.images}
                    onChange={(imgs) => setFormData({ ...formData, images: imgs })}
                  />
                </div>
              </div>
              <div>
                <Label>Sizes / Variants</Label>
                <div className="mt-2">
                  <VariantsEditor
                    value={formData.variants}
                    onChange={(v) => setFormData({ ...formData, variants: v })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="add-ingredients">Ingredients</Label>
                <textarea
                  id="add-ingredients"
                  value={formData.ingredients}
                  onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                  rows={3}
                  className="w-full mt-2 px-3 py-2 rounded-lg bg-input border border-border text-foreground"
                />
              </div>
              <div className="flex gap-4 pt-4 sticky bottom-0 bg-card border-t border-border -mx-6 px-6 pb-2 mt-2">
                <Button onClick={handleCreate} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="create-product-btn">
                  Create Product
                </Button>
                <Button onClick={() => setAddDialog(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
